import {
  normalizeFrontmatter,
  slugFromPath,
  sortPublished,
  type BlogFrontmatter,
} from '../blog/meta';

/**
 * Frontmatter contract for content/alternatives/*.mdx. Extends the blog
 * frontmatter with the named competitor (used in the breadcrumb, OG alt text,
 * and comparison framing). The header/title never names AnyAPI.
 *
 * Exported because `AlternativePostMeta` names it, and the route tree's declared
 * types cannot reference a type it cannot name.
 *
 * @public
 */
export interface AlternativeFrontmatter extends BlogFrontmatter {
  /** competitor display name, e.g. "Apify" */
  competitor: string;
}

/** An alternatives post without its compiled body. */
export interface AlternativePostMeta {
  slug: string;
  frontmatter: AlternativeFrontmatter;
}

// Frontmatter only, for the same reason as the blog glob in `../blog/meta`: the
// three compiled comparison bodies used to be inlined into the shared entry
// chunk because this module was reachable from a route `loader`.
const frontmatters = import.meta.glob<Partial<AlternativeFrontmatter>>(
  '/content/alternatives/*.mdx',
  { eager: true, query: '?frontmatter', import: 'default' },
);

const ALL: AlternativePostMeta[] = sortPublished(
  Object.entries(frontmatters).map(([path, fm]) => {
    const slug = slugFromPath(path);
    return {
      slug,
      frontmatter: { ...normalizeFrontmatter(fm, slug), competitor: fm?.competitor ?? '' },
    };
  }),
);

const BY_SLUG = new Map(ALL.map((p) => [p.slug, p]));

/** All published alternatives posts, newest first, without their bodies. */
export function listAlternativeMeta(): AlternativePostMeta[] {
  return ALL;
}

/** Look up one published alternatives post's metadata by slug. */
export function getAlternativeMeta(slug: string): AlternativePostMeta | undefined {
  return BY_SLUG.get(slug);
}

/** Canonical URL for an alternatives post. */
export function altUrl(slug: string): string {
  return `https://getanyapi.com/alternatives/${slug}`;
}
